import React from "react";
import { Link } from "react-router-dom";
import { Download, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { SemearFontStyle, YHUHName, YHUSHAName } from "../components/SemearNames";

export default function Declaration() {
  return (
    <div className="min-h-screen" data-testid="declaration-page">
      <SemearFontStyle />

      {/* Hero */}
      <section className="bg-royal-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/governance"
            className="inline-flex items-center text-slate-400 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Governance
          </Link>

          <h1 className="heading-hero">Declaration</h1>

          <p className="text-slate-300 mt-4">
            The official declaration of The Kingdom of Yunah'Shach'Qal Yashar'al as a sovereign micro-nation.
          </p>
        </div>
      </section>

      {/* Document Content */}
      <section className="bg-parchment py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border-l-4 border-kingdom-gold p-4 mb-8 rounded-sm">
            <p className="text-sm text-slate-600">
              <strong>Document Integrity:</strong> Presented from the official declaration; formatting improvements only for website readability.
            </p>
          </div>

          <div className="bg-white shadow-xl rounded-sm p-8 md:p-12">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-royal-blue mb-2">
                DECLARATION
              </h2>

              <h3 className="font-heading text-xl md:text-2xl text-royal-blue">
                of THE KINGDOM
                <br />
                of Yunah'Shach'Qal Yashar'al
              </h3>
            </div>

            <div className="prose prose-lg max-w-none text-slate-700 space-y-6 governance-text">
              <p>
                On this date of one, year two thousand twenty four on this fourteenth day of the Fourth Month, let it be known that we have formally formed the private Nation of:{" "}
                <strong>THE KINGDOM of YUNAH’SHACH’QAL YAHSHAR’AL</strong>
              </p>

              <p>
                <strong>Located:</strong> 777 QADASH LANE, YUNAH’SHACH’QAL, YASHAR’AL
              </p>

              <p>
                yaramYahu BanYaHUaH and shaba sharah BatYaHUaH stand as appointed guardians and stewards of Yunah'Shach'Qal Yashar'al, weighing all major matters with the elders and council under Turah; their service continues unless they willingly step down or their physical bodies return to the dust, and any succession shall be appointed through the same council order rather than by unchecked bloodline rule. Unless appointed, Sharah tashuYah BatYaHUaH shall be in charge under the same conditions and so let it be known.
              </p>

              <h3 className="font-heading text-xl font-bold text-royal-blue mt-8">
                MISSION
              </h3>

              <p>
                The Kingdom of Yunah'Shach'Qal Yashar'al is not established to replace the Kingdom of <YHUHName /> (YaHUaH), but to serve as a gathering place for those seeking to walk according to His Instructions while preparing themselves, their households, and future generations for His coming Kingdom.
              </p>

              <p>
                The mission of the Kingdom of Yunah'Shach'Qal Yashar'al is to seek the Ancient Path, restore obedience to the Turah of <YHUHName />, proclaim the Kingdom of Ha'Shamaym, and gather together those whom <YHUHName /> is calling out from among the nations.
              </p>

              <p>
                We seek to equip individuals, households, and communities to walk according to the Instructions of <YHUHName /> through teaching, discipleship, service, stewardship, and righteous living.
              </p>

              <p>
                We labor to restore the ancient foundations of family, agriculture, craftsmanship, knowledge, wisdom, self-governance, community responsibility, and covenant life according to Scripture.
              </p>

              <p>
                We strive to preserve and teach the Abary tongue, ancient history, genealogies, manuscripts, testimonies, and knowledge entrusted to our generation.
              </p>

              <p>
                We seek to establish communities capable of providing for their needs through righteous labor, wise stewardship, useful trades, agriculture, and mutual support, thereby reducing dependence upon systems that oppose the Instructions of <YHUHName />.
              </p>

              <p>
                We encourage every member to develop spiritual maturity, practical skills, and a servant's heart so that each household may become a source of strength and provision within the assembly.
              </p>

              <p>
                We seek to care for widows, orphans, the poor, the stranger, and those in need, following the examples given throughout Scripture.
              </p>

              <p>
                We proclaim that <YHUHName /> alone is Sovereign, that <YHUSHAName /> (YaHUSHA) is Mashyach and Kinsman Redeemer, and that every area of life is to be brought into obedience to the will of <YHUHName />.
              </p>

              <p>
                Above all, the mission of the Kingdom of Yunah'Shach'Qal Yashar'al is to prepare a people who walk in obedience, truth, wisdom, and endurance before <YHUHName />.
              </p>

              <h3 className="font-heading text-xl font-bold text-royal-blue mt-8">
                ORDER OF THE KINGDOM
              </h3>

              <p>
                The Kingdom of Yunah'Shach'Qal Yashar'al recognizes <YHUHName /> alone as Malak, Lawgiver, and Judge.
              </p>

              <p>
                The Turah of <YHUHName /> is the standard by which the Kingdom seeks to walk, judge matters, settle disputes, appoint stewards, and order community life.
              </p>

              <p>
                The Kingdom is not built upon political parties, elections, political offices, or the traditions of the nations. Rather, it seeks to follow the pattern established in Scripture through elders, judges, heads of households, overseers, and humble, trustworthy servants.
              </p>

              <p>
                Every household bears responsibility before <YHUHName /> for the instruction, provision, and care of those entrusted to it.
              </p>

              <p>
                Every community retains stewardship over its daily affairs while remaining united with the wider Kingdom through covenant, shared purpose, mutual support, and obedience to the Turah.
              </p>

              <ul className="list-disc pl-6 space-y-2">
                <li>Elders provide counsel, discernment, correction, and guidance.</li>
                <li>Judges hear matters brought before them and seek righteous judgment according to the Turah.</li>
                <li>Overseers coordinate works entrusted to their care.</li>
                <li>Heads of households shepherd their families in righteousness and service.</li>
              </ul>

              <p>
                No appointment shall be obtained through wealth, popularity, political influence, inheritance, or self-exaltation. Those who serve are to be examined according to their conduct, fruits, wisdom, and trustworthiness.
              </p>

              <p>
                The Kingdom seeks unity without oppression, order without tyranny, and stewardship without domination.
              </p>

              <p>
                The purpose of this order is not the accumulation of power, but the well-being of the people, the preservation of righteousness, the care of households, and the kabud of <YHUHName /> in all things.
              </p>

              <p>
                As the Kingdom grows, communities, settlements, assemblies, and households may join themselves to this covenant, retaining stewardship over local matters while walking together according to the shared covenant, Turah, and mission of the Kingdom.
              </p>

              <p>
                All matters are ultimately subject to <YHUHName />, whose Kingdom endures forever.
              </p>

              <h3 className="font-heading text-xl font-bold text-royal-blue mt-8">
                ECONOMY SECTION
              </h3>

              <p>
                Utilizing sound money, such as gold and silver, is something that's been practiced by humankind for thousands of years as a way of storing economic energy. Today, we even have the technology to use gold and silver as money in previously unusable denominations.
              </p>

              <p>
                We believe that utilizing gold and silver, with their value being derived from the commodity itself, is the only way to maintain a stable monetary system. Conventional nations don't use money but instead use various forms of fiat currency, such as the pound, euro, dollar and so on. These are all created out of thin air, entirely trust based and constantly losing value through the limitless expansion of the currency supply. If a nation uses currency in place of money, then that nation will face economic collapse sooner or later.
              </p>

              <p>
                We will be promoting sound money through the acquisition and utilization of gold and silver stater bars and coins which feature The Kingdom of Yunah’Shach’Qal Yahshar’al and the member states of the Empire. These staters will be circulated across The Kingdom of Yunah’Shach’Qal Yahshar’al as a medium of exchange.
              </p>

              <p>
                Utilizing sound money ensures a means of stability for The Kingdom. The Kingdom of Yunah’Shach’Qal Yahshar’al utilizes sound money because it is a clear break from the established global monetary system that we all live in today. Utilizing sound money also protects the wealth of our YaSHaRUN. We also utilize sound money because it has universal value. Sound money is extremely convertible into any fiat currency.
              </p>

              <p>
                Sound money will gradually be introduced into The Kingdom of Yunah’Shach’Qal Yahshar’al until we have built up our supply of sound money. We will start off utilizing the existing fiat currency. Once our sound money supply is built up, it will be our sole money system used.
              </p>

              <h3 className="font-heading text-xl font-bold text-royal-blue mt-8">
                LAWS &amp; RESTRICTIONS
              </h3>

              <div className="bg-parchment p-6 rounded-sm border-l-4 border-kingdom-gold">
                <p className="italic">
                  And ALAHYM spoke all these Words, saying,
                  <br />
                  “I am <YHUHName /> your ALAHYM, who brought you out of the land of Mitsrayim, out of the house of slavery.
                  <br />
                  “You have no other mighty ones against My face.
                  <br />
                  “You do not make for yourself a carved image, or any likeness of that which is in the heavens above, or which is in the earth beneath, or which is in the waters under the earth,
                  <br />
                  you do not bow down to them nor serve them.
                  <br />
                  “Remember the Shabat day, to make it qadash.
                  <br />
                  “Six days you labour, and shall do all your work,
                  <br />
                  but the seventh day is a Shabat of <YHUHName /> your ALAHYM...”
                </p>

                <p className="mt-4 text-sm text-slate-600">
                  — Shamut 20:1-17
                </p>
              </div>

              <h3 className="font-heading text-xl font-bold text-royal-blue mt-8">
                BELIEFS, VALUES &amp; NATION HISTORY
              </h3>

              <p>
                The Kingdom of Yunah'Shach'Qal Yashar'al acknowledges <YHUHName />, Creator of Ha'Shamaym and Aratz, as the only true Sovereign, Lawgiver, Judge, and Malak.
              </p>

              <p>
                We acknowledge <YHUSHAName /> as the promised Mashyach, the Lamb who was slain, our Kinsman Redeemer, and the one through whom reconciliation to <YHUHName /> has been made available.
              </p>

              <p>
                We acknowledge the Ruch Ha'Qadash sent forth by <YHUHName /> to guide, teach, convict, and strengthen His people.
              </p>

              <p>
                The Kingdom of Yunah'Shach'Qal Yashar'al exists for the purpose of returning to the Ancient Path spoken of in YaramYahu 6:16: “Stand in the ways and see, and ask for the old paths, where the tub way is, and walk in it.”
              </p>

              <h3 className="font-heading text-xl font-bold text-royal-blue mt-8">
                YaSHaRUN OF THE KINGDOM OF YUNAH'SHACH'QAL YASHAR'AL
              </h3>

              <p>
                The Kingdom of Yunah'Shach'Qal Yashar'al is a covenant people gathered under the power of YaHUaH, the redemption of <YHUSHAName />, and the guidance of the Ruch Ha'Qadash.
              </p>

              <p>
                Nationality within the Kingdom is a covenant relationship and not a commercial transaction. No person shall purchase nationality through money, gifts, influence, status, titles, possessions, or personal connections.
              </p>

              <h3 className="font-heading text-xl font-bold text-royal-blue mt-8">
                FINAL DECREE
              </h3>

              <p className="text-lg font-semibold text-kingdom-gold">
                “To the 12 Tribes scattered, Go and seek the Ancient Path of YaHUaH and be made free”
              </p>

              <p className="italic">
                Thus said <YHUHName /> “Stand in the ways and see, and ask for the old paths, where the good way is, and walk in it; and find rest for yourselves.”
              </p>

              <p className="text-sm text-slate-600">
                — YaramYahu 6:1
                6
              </p>
            </div>

            <div className="mt-12 pt-8 border-t border-slate-200 text-center">
              <a
                href="/documents/Micronation-Declaration-form-Yahsharal.pdf"
                download
              >
                <Button className="btn-gold">
                  <Download className="w-4 h-4 mr-2" />
                  Download Official Declaration
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
